import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { Text, ContentLoader } from 'shared/components'
import { useInfiniteScroll } from 'shared/hooks'

import MobileBeneficiaryCard from 'routes/profile/beneficiaries/components/mobileBeneficiaryCard/component'

import { BottomNavigation } from 'routes/public/events/overview/components/bottomNavigation'
import { MobileInwinsHeader } from '../mobileInwinsHeader'
import { MobileInwinCard } from '../mobileInwinCard'
import { MobileInwinsEmptyState } from '../mobileInwinsEmptyState'
import * as styles from './styles'

const MobileInwinsLayoutComponent = ({
	loadBets,
	loadBeneficiariesFromInwins,
	followerService,
}) => {
	const [activeTab, setActiveTab] = useState('inwins')
	const [inwinsData, setInwinsData] = useState({
		content: [],
		loading: false,
	})
	const [beneficiariesData, setBeneficiariesData] = useState({
		content: [],
		loading: false,
	})
	const [pagination, setPagination] = useState({
		page: 0,
		hasMore: true,
		loadingMore: false,
	})
	const [followedBeneficiaries, setFollowedBeneficiaries] = useState(new Set())

	// Ref to track current pagination state to avoid race conditions
	const paginationRef = useRef(pagination)

	// Update ref whenever pagination changes
	useEffect(() => {
		paginationRef.current = pagination
	}, [pagination])

	const loadInitialInwinsData = async () => {
		setInwinsData((prev) => ({ ...prev, loading: true }))
		setPagination({ page: 0, hasMore: true, loadingMore: false })

		try {
			const result = await loadBets({ page: 0, pageSize: 20 }, null, {
				search: '',
			})

			const content = result?.content || []
			setInwinsData({ content, loading: false })

			// Has more items if API returned full page size
			const hasMoreItems = (content.length || 0) === 20

			setPagination((prev) => ({
				...prev,
				page: 0,
				hasMore: hasMoreItems,
			}))
		} catch (error) {
			setInwinsData((prev) => ({ ...prev, loading: false }))
		}
	}

	const loadInitialBeneficiariesData = async () => {
		setBeneficiariesData((prev) => ({ ...prev, loading: true }))
		setPagination({ page: 0, hasMore: true, loadingMore: false })

		try {
			// Load beneficiaries from Inwins instead of favorite beneficiaries
			const result = await loadBeneficiariesFromInwins({
				page: 0,
				pageSize: 20,
			})

			const content = result?.content || []
			setBeneficiariesData({ content, loading: false })

			// Load actual follow status for these beneficiaries
			try {
				const followedResponse = await followerService.getBeneficiariesFollowers({
					page: 0,
					size: 1000,
				})

				const followedBeneficiaryIds = new Set()
				if (followedResponse?.content) {
					followedResponse.content.forEach(followed => {
						const id = followed.beneficiaryDTO?.id || followed.id
						if (id) {
							followedBeneficiaryIds.add(id)
						}
					})
				}

				setFollowedBeneficiaries(followedBeneficiaryIds)
			} catch (followError) {
				console.warn('Failed to load beneficiary follow status:', followError)
				// If we can't load follow status, assume none are followed
				setFollowedBeneficiaries(new Set())
			}

			// Has more items if API returned full page size
			const hasMoreItems = (content.length || 0) === 20

			setPagination((prev) => ({
				...prev,
				page: 0,
				hasMore: hasMoreItems,
			}))
		} catch (error) {
			setBeneficiariesData((prev) => ({ ...prev, loading: false }))
		}
	}

	const loadMoreInwinsData = useCallback(async () => {
		const currentPagination = paginationRef.current

		if (currentPagination.loadingMore || !currentPagination.hasMore) {
			return
		}

		const nextPage = currentPagination.page + 1
		setPagination((prev) => ({ ...prev, loadingMore: true }))

		try {
			const result = await loadBets({ page: nextPage, pageSize: 20 }, null, {
				search: '',
			})

			const newContent = result?.content || []

			setInwinsData((prev) => {
				const existingIds = new Set(prev.content.map((item) => item.id))
				const uniqueNewContent = newContent.filter(
					(item) => !existingIds.has(item.id)
				)

				return {
					...prev,
					content: [...prev.content, ...uniqueNewContent],
				}
			})

			const hasMoreItems = newContent.length === 20

			setPagination((prev) => ({
				...prev,
				page: nextPage,
				hasMore: hasMoreItems,
				loadingMore: false,
			}))
		} catch (error) {
			setPagination((prev) => ({ ...prev, loadingMore: false }))
		}
	}, [loadBets])

	const loadMoreBeneficiariesData = useCallback(async () => {
		const currentPagination = paginationRef.current

		if (currentPagination.loadingMore || !currentPagination.hasMore) {
			return
		}

		const nextPage = currentPagination.page + 1
		setPagination((prev) => ({ ...prev, loadingMore: true }))

		try {
			const result = await loadBeneficiariesFromInwins({
				page: nextPage,
				pageSize: 20,
			})

			const newContent = result?.content || []

			// Check follow status for newly loaded beneficiaries
			try {
				const followedResponse = await followerService.getBeneficiariesFollowers({
					page: 0,
					size: 1000,
				})

				const followedBeneficiaryIds = new Set()
				if (followedResponse?.content) {
					followedResponse.content.forEach(followed => {
						const id = followed.beneficiaryDTO?.id || followed.id
						if (id) {
							followedBeneficiaryIds.add(id)
						}
					})
				}

				setFollowedBeneficiaries(followedBeneficiaryIds)
			} catch (followError) {
				console.warn('Failed to load beneficiary follow status:', followError)
			}

			setBeneficiariesData((prev) => {
				const existingIds = new Set(prev.content.map((item) => item.id))
				const uniqueNewContent = newContent.filter(
					(item) => !existingIds.has(item.id)
				)

				return {
					...prev,
					content: [...prev.content, ...uniqueNewContent],
				}
			})

			const hasMoreItems = newContent.length === 20

			setPagination((prev) => ({
				...prev,
				page: nextPage,
				hasMore: hasMoreItems,
				loadingMore: false,
			}))
		} catch (error) {
			setPagination((prev) => ({ ...prev, loadingMore: false }))
		}
	}, [activeTab, loadBeneficiariesFromInwins, followerService])

	// Load initial data when component mounts or tab changes
	useEffect(() => {
		if (activeTab === 'inwins') {
			loadInitialInwinsData()
		} else if (activeTab === 'beneficiaries') {
			loadInitialBeneficiariesData()
		}
	}, [activeTab])

	useInfiniteScroll({
		enabled: true,
		getPagination: () => paginationRef.current,
		getContentLength: () =>
			activeTab === 'inwins'
				? inwinsData.content.length
				: beneficiariesData.content.length,
		loadMoreData: () =>
			activeTab === 'inwins'
				? loadMoreInwinsData()
				: loadMoreBeneficiariesData(),
	})

	const handleTabChange = (tabId) => {
		if (tabId !== activeTab) {
			setActiveTab(tabId)
		}
	}

	const handleFavoriteToggle = async (beneficiary, isCurrentlyFollowed) => {
		const beneficiaryId = beneficiary.beneficiaryDTO?.id || beneficiary.id

		try {
			// Update local state for instant UI feedback
			setFollowedBeneficiaries((prev) => {
				const newSet = new Set(prev)
				if (isCurrentlyFollowed) {
					newSet.delete(beneficiaryId)
				} else {
					newSet.add(beneficiaryId)
				}
				return newSet
			})

			if (isCurrentlyFollowed) {
				await followerService.removeFollowers({
					beneficiaryIds: [beneficiaryId],
				})
			} else {
				await followerService.addFollowers({
					beneficiaryIds: [beneficiaryId],
				})
			}

			// Refresh the data
			if (activeTab === 'beneficiaries') {
				loadInitialBeneficiariesData()
			}
			} catch (error) {
				console.error('Failed to toggle favorite beneficiary:', error)
				// Revert local state on error
				setFollowedBeneficiaries((prev) => {
					const newSet = new Set(prev)
					if (isCurrentlyFollowed) {
						newSet.add(beneficiaryId)
					} else {
						newSet.delete(beneficiaryId)
					}
					return newSet
			})
		}
	}

	const renderContent = () => {
		if (activeTab === 'inwins') {
			if (inwinsData.loading) {
				return <ContentLoader />
			}

			if (inwinsData.content.length === 0) {
				return <MobileInwinsEmptyState />
			}

			return (
				<div css={[styles.cardsContainer, styles.inwinsCardsContainer]}>
					{inwinsData.content.map((inwin) => (
						<MobileInwinCard key={inwin.id} inwin={inwin} />
					))}
					{pagination.loadingMore && (
						<div css={styles.loadingMore}>
							<ContentLoader waiting={true}>
								<div css={styles.loadingPlaceholder}>
									<Text size={14} gray>
										Loading more InWins...
									</Text>
								</div>
							</ContentLoader>
						</div>
					)}
				</div>
			)
		}

		// Beneficiaries tab
		if (beneficiariesData.loading) {
			return <ContentLoader />
		}

		if (beneficiariesData.content.length === 0) {
			return (
				<div css={styles.emptyState}>
					<Text size={16} gray>
						No beneficiaries found in your Inwins history
					</Text>
				</div>
			)
		}

		return (
			<div css={styles.cardsContainer}>
				{beneficiariesData.content.map((beneficiary) => (
					<MobileBeneficiaryCard
						key={beneficiary.id}
						beneficiary={beneficiary}
						isFavorite={true}
						isFollowed={followedBeneficiaries.has(
							beneficiary.id || beneficiary.beneficiaryDTO?.id
						)}
						activeFilter="favorite"
						onFavoriteToggle={handleFavoriteToggle}
					/>
				))}
				{pagination.loadingMore && (
					<div css={styles.loadingMore}>
						<ContentLoader waiting={true}>
							<div css={styles.loadingPlaceholder}>
								<Text size={14} gray>
									Loading more beneficiaries from Inwins...
								</Text>
							</div>
						</ContentLoader>
					</div>
				)}
			</div>
		)
	}

	return (
		<>
			<MobileInwinsHeader
				title="History"
				activeTab={activeTab}
				onTabChange={handleTabChange}
			/>

			<div css={styles.contentContainer}>
				<ContentLoader
					waiting={
						(activeTab === 'inwins'
							? inwinsData.loading
							: beneficiariesData.loading) || false
					}
				>
					{renderContent()}
				</ContentLoader>
			</div>

			<BottomNavigation />
		</>
	)
}

MobileInwinsLayoutComponent.propTypes = {
	loadBets: PropTypes.func.isRequired,
	loadBeneficiariesFromInwins: PropTypes.func.isRequired,
	followerService: PropTypes.object.isRequired,
}

export default MobileInwinsLayoutComponent
